'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { MdWarning } from 'react-icons/md';
import { GrNodes } from 'react-icons/gr';
import { RiDatabaseLine } from 'react-icons/ri';
import { match } from 'ts-pattern';
import { z } from 'zod';

import { Button } from '@/components/button/button';
import { Menu, MenuItem } from '@/components/dropdownMenu/dropdownMenu';
import { Icon } from '@/components/icon/icon';
import Modal from '@/components/modal/modal';
import { Heading } from '@/components/typography/heading';
import { Text } from '@/components/typography/text';
import { useQueryClient } from '@tanstack/react-query';

import { useSecretCreate, useSecretUpdate } from '@/hooks/mutations/useSecretMutations';
import { secretsKeys, useSecretDetailQuery, useSecretsQuery } from '@/hooks/queries/useSecrets';
import { useAuthContext } from '@/hooks/useAuthContext';
import { SecretDetail, SecretListItem, SecretTypeEnum } from '@/types/secret';

import { ManageAccessModal } from './manageAccessModal';
import styles from './secretModal.module.scss';
import { charCounterText, generateEnvVarName, getExistingEnvVars } from './secretHelpers';


// --- Constants ---

const MAX_NAME_LENGTH = 50;
const MAX_DESCRIPTION_LENGTH = 150;
const MAX_VALUE_BYTES = 64 * 1024; // 64 KB

const NAME_REGEX = /^[a-z0-9.-]+$/;

const TYPE_META: Record<SecretTypeEnum, { label: string; description: string; icon: typeof GrNodes }> = {
  [SecretTypeEnum.AGENT]: { label: 'Constellation', description: 'Deployed agentic systems', icon: GrNodes },
  [SecretTypeEnum.DATA_PLATFORM]: { label: 'Data Platform', description: 'Your data available inside KanpAI™', icon: RiDatabaseLine },
  [SecretTypeEnum.EVALS]: { label: 'Evaluation', description: 'AI assessment tests and metrics', icon: GrNodes }
};

// Only Constellation and Data Platform are available for selection (Evaluation excluded for now)
const SELECTABLE_TYPES = [ SecretTypeEnum.AGENT, SecretTypeEnum.DATA_PLATFORM ] as const;


// --- Schema ---

function createSecretFormSchema( mode: 'create' | 'update' ) {
  return z.object( {
    type: z.nativeEnum( SecretTypeEnum, { message: 'Secret type is required' } ),
    name: z.string()
      .min( 1, 'Secret name is required' )
      .max( MAX_NAME_LENGTH, `Maximum ${ MAX_NAME_LENGTH } characters` )
      .regex( NAME_REGEX, 'Lowercase letters (a\u2013z), numbers (0\u20139), hyphens (-), and dots (.) only.' ),
    description: z.string().max( MAX_DESCRIPTION_LENGTH ).optional().or( z.literal( '' ) ),
    value: mode === 'create'
      ? z.string()
        .min( 1, 'Value is required' )
        .refine( v => new Blob( [v] ).size <= MAX_VALUE_BYTES, 'Secret value exceeds the 64 KB size limit.' )
      : z.string()
        .refine( v => !v || new Blob( [v] ).size <= MAX_VALUE_BYTES, 'Secret value exceeds the 64 KB size limit.' )
  } );
}

type SecretFormData = z.infer<ReturnType<typeof createSecretFormSchema>>;


// --- Types ---

type SubmitResult =
  | { status: 'success'; secretType: SecretTypeEnum }
  | { status: 'error'; message: string };

type ModalStep = 'form' | 'result';

type SecretModalProps = {
  mode: 'create' | 'update';
  isOpen: boolean;
  onClose: () => void;
  secret?: SecretListItem;
};


// --- Component ---

export function SecretModal( { mode, isOpen, onClose, secret }: SecretModalProps ) {
  const [ step, setStep ] = useState<ModalStep>( 'form' );
  const [ submitResult, setSubmitResult ] = useState<SubmitResult | null>( null );
  const [ valueVisible, setValueVisible ] = useState( false );
  const [ duplicateNameError, setDuplicateNameError ] = useState<string | null>( null );
  const [ createdSecret, setCreatedSecret ] = useState<SecretDetail | null>( null );
  const [ showManageAccessModal, setShowManageAccessModal ] = useState( false );

  const queryClient = useQueryClient();
  const { sessionContext } = useAuthContext();
  const userId = sessionContext?.userId ?? '';

  const { data: secrets } = useSecretsQuery();
  const createMutation = useSecretCreate();
  const updateMutation = useSecretUpdate();

  const { data: secretDetail } = useSecretDetailQuery(
    mode === 'update' ? secret?.id : undefined
  );

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isDirty, isValid }
  } = useForm<SecretFormData>( {
    resolver: zodResolver( createSecretFormSchema( mode ) ),
    defaultValues: {
      type: undefined as unknown as SecretTypeEnum,
      name: '',
      description: '',
      value: ''
    },
    mode: 'onChange'
  } );

  const watchedType = watch( 'type' );
  const watchedName = watch( 'name' );
  const watchedDescription = watch( 'description' );

  // Pre-populate form in update mode
  useEffect( () => {
    if ( mode === 'update' && secretDetail && secret ) {
      reset( {
        type: secret.type,
        name: secretDetail.name,
        description: secretDetail.description ?? secret.description ?? '',
        value: '' // Value is always empty in update mode per AC
      } );
    }
  }, [ mode, secretDetail, secret, reset ] );

  // Reset state when modal opens/closes
  useEffect( () => {
    if ( isOpen ) {
      setStep( 'form' );
      setSubmitResult( null );
      setValueVisible( false );
      setDuplicateNameError( null );
      setCreatedSecret( null );
      setShowManageAccessModal( false );
      if ( mode === 'create' ) {
        reset( {
          type: undefined as unknown as SecretTypeEnum,
          name: '',
          description: '',
          value: ''
        } );
      }
    }
  }, [ isOpen, mode, reset ] );

  // Duplicate name validation (on name change with debounce)
  useEffect( () => {
    if ( !watchedName || !secrets ) {
      setDuplicateNameError( null );
      return;
    }

    const timer = setTimeout( () => {
      const isDuplicate = secrets.some( s => {
        if ( mode === 'update' && secret && s.id === secret.id ) return false;
        return s.name === watchedName;
      } );
      setDuplicateNameError(
        isDuplicate ? 'This Secret Name already exists. Choose a different name.' : null
      );
    }, 300 );

    return () => clearTimeout( timer );
  }, [ watchedName, secrets, mode, secret ] );

  const isPending = createMutation.isPending || updateMutation.isPending;

  const isFormValid = useMemo( () => {
    if ( !isValid || !!duplicateNameError ) return false;
    if ( mode === 'update' && !isDirty ) return false;
    return true;
  }, [ isValid, duplicateNameError, mode, isDirty ] );

  const onSubmit = useCallback( async( data: SecretFormData ) => {
    try {
      if ( mode === 'create' ) {
        const envVarName = data.type === SecretTypeEnum.AGENT
          ? generateEnvVarName( data.name, getExistingEnvVars( secrets ?? [] ) )
          : undefined;

        const created = await createMutation.mutateAsync( {
          name: data.name,
          value: data.value,
          userId,
          type: data.type,
          description: data.description || undefined,
          envVarName
        } );
        setCreatedSecret( created );
      } else if ( secret ) {
        await updateMutation.mutateAsync( {
          secretId: secret.id,
          body: {
            value: data.value || '',
            userId,
            name: data.name,
            description: data.description || undefined
          }
        } );
      }

      setSubmitResult( { status: 'success', secretType: data.type } );
      setStep( 'result' );
    } catch {
      setSubmitResult( {
        status: 'error',
        message: 'A platform error occurred. Please try again later.'
      } );
      setStep( 'result' );
    }
  }, [ mode, secret, secrets, userId, createMutation, updateMutation ] );

  // Close after successful operation — refresh the secrets list
  const handleResultClose = useCallback( () => {
    if ( submitResult?.status === 'success' ) {
      queryClient.invalidateQueries( { queryKey: secretsKeys.all } );
    }
    onClose();
  }, [ submitResult, queryClient, onClose ] );

  // Cancel — reset form to original state and close
  const handleCancel = useCallback( () => {
    if ( mode === 'update' && secretDetail && secret ) {
      reset( {
        type: secret.type,
        name: secretDetail.name,
        description: secretDetail.description ?? secret.description ?? '',
        value: ''
      } );
    }
    onClose();
  }, [ mode, secretDetail, secret, reset, onClose ] );

  // ESC key handler
  useEffect( () => {
    if ( !isOpen ) return;

    const handleKeyDown = ( e: KeyboardEvent ) => {
      if ( e.key === 'Escape' ) {
        if ( step === 'result' ) {
          handleResultClose();
        } else {
          handleCancel();
        }
      }
    };

    document.addEventListener( 'keydown', handleKeyDown );
    return () => document.removeEventListener( 'keydown', handleKeyDown );
  }, [ isOpen, step, handleResultClose, handleCancel ] );

  if ( !isOpen ) return null;

  const nameCharCounter = charCounterText( watchedName?.length ?? 0, MAX_NAME_LENGTH );
  const descCharCounter = charCounterText( watchedDescription?.length ?? 0, MAX_DESCRIPTION_LENGTH );

  const selectedTypeInfo = watchedType ? TYPE_META[watchedType] : null;

  // --- Result step ---
  if ( step === 'result' && submitResult ) {
    return (
      <Modal className={ styles.modal }>
        <div className={ styles.result_layout }>
          <div className={ styles.result_content }>
            {match( submitResult )
              .with( { status: 'error' }, ( { message } ) => (
                <>
                  <Heading as="h2" size="md" className={ styles.result_title }>
                    { mode === 'create' ? 'Secret was not created' : 'Secret was not updated' }
                    {' '}<MdWarning className={ styles.result_icon_error } />
                  </Heading>
                  <Text size="md" className={ styles.result_message }>{ message }</Text>
                </>
              ) )
              .with( { status: 'success', secretType: SecretTypeEnum.DATA_PLATFORM }, () => (
                <>
                  <Heading as="h2" size="md" className={ styles.result_title }>
                    { mode === 'create' ? 'Secret Created' : 'Secret Updated' }
                  </Heading>
                  <Text size="md" className={ styles.result_message }>
                    Your <strong>data platform secret</strong> was successfully { mode === 'create' ? 'created' : 'updated' }.
                  </Text>
                </>
              ) )
              .with( { status: 'success', secretType: SecretTypeEnum.AGENT }, () => (
                <>
                  <Heading as="h2" size="md" className={ styles.result_title }>
                    { mode === 'create' ? 'Secret Created' : 'Secret Updated' }
                  </Heading>
                  { mode === 'create' ? (
                    <>
                      <Text size="md" className={ styles.result_message }>
                        Your <strong>constellation secret</strong> was successfully created.
                      </Text>
                      <Text size="md" className={ styles.result_message }>
                        By default, no constellations can use this secret.
                      </Text>
                      <Text size="md" className={ styles.result_message }>
                        Grant constellations access to start using this secret.
                      </Text>
                    </>
                  ) : (
                    <Text size="md" className={ styles.result_message }>
                      Your <strong>constellation secret</strong> was successfully updated.
                    </Text>
                  ) }
                </>
              ) )
              .otherwise( () => (
                <>
                  <Heading as="h2" size="md" className={ styles.result_title }>
                    { mode === 'create' ? 'Secret Created' : 'Secret Updated' }
                  </Heading>
                  <Text size="md" className={ styles.result_message }>
                    Your secret was successfully { mode === 'create' ? 'created' : 'updated' }.
                  </Text>
                </>
              ) )
            }
          </div>
          <div className={ styles.result_actions }>
            <Button variant="secondary" size="sm" onClick={ handleResultClose }>
              Close
            </Button>
            { mode === 'create' && submitResult.status === 'success' && submitResult.secretType === SecretTypeEnum.AGENT && createdSecret && (
              <Button variant="primary" size="sm" onClick={ () => setShowManageAccessModal( true ) }>
                Manage Access
              </Button>
            ) }
          </div>
          { createdSecret && (
            <ManageAccessModal
              isOpen={ showManageAccessModal }
              onClose={ () => {
                setShowManageAccessModal( false );
                handleResultClose();
              } }
              secret={ createdSecret }
            />
          ) }
        </div>
      </Modal>
    );
  }

  // --- Form step ---
  return (
    <Modal className={ styles.modal }>
      <div className={ styles.modal_content }>
        <div className={ styles.modal_header }>
          <Heading as="h2" size="md" className={ styles.title }>
            { mode === 'create' ? 'Add Managed Secret' : 'Update Managed Secret' }
          </Heading>
          <Text size="md" className={ styles.subtitle }>
            Store a secret and define how it is used.
          </Text>
        </div>

        <form className={ styles.form } onSubmit={ handleSubmit( onSubmit ) }>
          <div className={ styles.scrollable_content }>

            {/* Secret Type */}
            <div className={ styles.form_field }>
              <div className={ styles.field_header }>
                <span className={ styles.field_label }>Secret Type</span>
                <span className={ styles.field_helper }>Defines how and where this secret can be used.</span>
              </div>
              { mode === 'create' ? (
                <div className={ styles.type_select_wrapper }>
                  <Menu
                    variant="select"
                    value={ selectedTypeInfo?.label ?? 'Select Type' }
                    data-testid="secret-type-select"
                  >
                    { SELECTABLE_TYPES.map( typeVal => {
                      const meta = TYPE_META[typeVal];
                      return (
                        <MenuItem
                          key={ typeVal }
                          value={ meta.label }
                          description={ meta.description }
                          leadingIcon={ <Icon icon={ meta.icon } size="sm" /> }
                          onClick={ () => setValue( 'type', typeVal, { shouldValidate: true, shouldDirty: true } ) }
                        />
                      );
                    } ) }
                  </Menu>
                </div>
              ) : (
                <div className={ styles.type_display } data-testid="secret-type-display">
                  { selectedTypeInfo && (
                    <>
                      <Icon icon={ selectedTypeInfo.icon } size="sm" />
                      <span>{ selectedTypeInfo.label }</span>
                      <span className={ styles.type_description }>{ selectedTypeInfo.description }</span>
                    </>
                  ) }
                </div>
              ) }
              { errors.type && <span className={ styles.validation_error }>{ errors.type.message }</span> }
            </div>

            {/* Secret Name */}
            <div className={ styles.form_field }>
              <div className={ styles.field_header }>
                <span className={ clsx( styles.field_label, ( errors.name || duplicateNameError ) && styles.field_label_error ) }>
                  Secret Name
                </span>
                { duplicateNameError ? (
                  <span className={ styles.validation_error }>{ duplicateNameError }</span>
                ) : nameCharCounter ? (
                  <span className={ styles.char_counter }>{ nameCharCounter }</span>
                ) : (
                  <span className={ styles.field_helper }>
                    Lowercase letters (a&ndash;z), numbers (0&ndash;9), hyphens (-), and dots (.) only. Max { MAX_NAME_LENGTH } characters.
                  </span>
                ) }
              </div>
              <input
                { ...register( 'name' ) }
                type="text"
                maxLength={ MAX_NAME_LENGTH }
                placeholder="Ex. my-agent-secret"
                className={ clsx(
                  styles.input_field,
                  styles.input_name,
                  { [styles.input_error]: errors.name || duplicateNameError }
                ) }
                data-testid="secret-name-input"
              />
              { errors.name && !duplicateNameError && (
                <span className={ styles.validation_error }>{ errors.name.message }</span>
              ) }
            </div>

            {/* Description */}
            <div className={ styles.form_field }>
              <div className={ styles.field_header }>
                <span className={ styles.field_label }>Description (optional)</span>
                { descCharCounter ? (
                  <span className={ styles.char_counter }>{ descCharCounter }</span>
                ) : (
                  <span className={ styles.field_helper_right }>Max { MAX_DESCRIPTION_LENGTH } characters</span>
                ) }
              </div>
              <input
                { ...register( 'description' ) }
                type="text"
                maxLength={ MAX_DESCRIPTION_LENGTH }
                className={ clsx( styles.input_field, styles.input_description ) }
                data-testid="secret-description-input"
              />
            </div>

            {/* Value */}
            <div className={ styles.form_field }>
              <div className={ styles.field_header }>
                <span className={ clsx( styles.field_label, errors.value && styles.field_label_error ) }>
                  Value
                </span>
                <span className={ styles.field_helper }>The secret value. This can be updated later.</span>
                { errors.value?.message?.includes( '64 KB' ) ? (
                  <span className={ styles.validation_error }>{ errors.value.message }</span>
                ) : (
                  <span className={ styles.char_counter }>Max size: 64 KB</span>
                ) }
              </div>
              <div className={ styles.textarea_wrapper }>
                <textarea
                  { ...register( 'value' ) }
                  className={ clsx( styles.textarea, errors.value && styles.textarea_error, !valueVisible && styles.textarea_masked ) }
                  rows={ 4 }
                  data-testid="secret-value-input"
                />
                <div
                  className={ styles.eye_toggle }
                  onClick={ () => setValueVisible( v => !v ) }
                  role="button"
                  tabIndex={ 0 }
                  onKeyDown={ e => { if ( e.key === 'Enter' || e.key === ' ' ) setValueVisible( v => !v ); } }
                  data-testid="secret-value-toggle"
                >
                  <Icon icon={ valueVisible ? FiEyeOff : FiEye } size="sm" />
                </div>
              </div>
              { errors.value && !errors.value.message?.includes( '64 KB' ) && (
                <span className={ styles.validation_error }>{ errors.value.message }</span>
              ) }
            </div>

          </div>

          {/* Footer */}
          <div className={ styles.modal_footer }>
            <div className={ styles.footer_actions }>
              { mode === 'create' && !isFormValid && (
                <span className={ styles.footer_hint }>Complete required fields to add a secret.</span>
              ) }
              <div className={ styles.footer_buttons }>
                <Button variant="secondary" size="sm" onClick={ handleCancel } type="button">
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  type="submit"
                  disabled={ !isFormValid || isPending }
                  loading={ isPending }
                >
                  { mode === 'create' ? 'Add Secret' : 'Update Secret' }
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
}
